import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { type BreakdownItem, BreakdownTable } from '@/components/breakdown-table'
import { useDateWindow } from '@/components/date-range-picker'
import { EngineFilterControl, parseEngineFilter } from '@/components/engine-filter'
import { shortenUrl } from '@/lib/format'
import {
  DEFAULT_TABLE,
  type EngineFilter,
  type TableState,
  usePagesBreakdown,
} from '@/lib/site-data'
import {
  FilterInput,
  SectionHeader,
  SiteSectionBody,
  TableSkeleton,
  useSiteScope,
} from '../-section'

export const Route = createFileRoute('/_app/$org/$host/pages/')({
  // In the url, so a link to the Bing half of this table opens on it.
  validateSearch: (search: Record<string, unknown>): { engine?: EngineFilter } => {
    const engine = parseEngineFilter(search.engine)
    return engine ? { engine } : {}
  },
  component: SitePagesPage,
})

function SitePagesPage() {
  const navigate = Route.useNavigate()
  const { org, host, range, site } = useSiteScope()
  const [table, setTable] = useState<TableState>(DEFAULT_TABLE)
  const { engine = 'all' } = Route.useSearch()
  const { data, isPending, isFetching } = usePagesBreakdown(site, range, table, engine)
  useDateWindow(data?.range)

  const items: BreakdownItem[] =
    data?.rows.map((row) => ({
      key: String(row.pageId),
      // The host is on every row and only costs width; the path is the value.
      label: shortenUrl(row.url, host),
      title: row.url,
      metrics: row,
    })) ?? []

  return (
    <>
      <SectionHeader
        title="Pages"
        filter={
          <>
            <FilterInput
              placeholder="Filter by URL"
              value={table.search}
              onChange={(search) => setTable({ ...table, search, offset: 0 })}
            />
            <EngineFilterControl
              value={engine}
              engines={data?.engines}
              onChange={(next) => {
                // A different engine is a different list, not page three of the old one.
                setTable({ ...table, offset: 0 })
                navigate({
                  search: (prev) => ({ ...prev, engine: next === 'all' ? undefined : next }),
                  replace: true,
                })
              }}
            />
          </>
        }
      />

      <SiteSectionBody
        org={org}
        host={host}
        range={range}
        result={data}
        isPending={isPending}
        // A filter that matches nothing has to leave the filter on screen, so
        // the section only counts as empty when nothing is being filtered.
        rowCount={table.search ? 1 : (data?.total ?? 0)}
        skeleton={<TableSkeleton />}
      >
        <BreakdownTable
          dimensionLabel="Page"
          items={items}
          total={data?.total ?? 0}
          table={table}
          onTableChange={setTable}
          emptyLabel="No pages match that filter."
          isFetching={isFetching}
          onRowSelect={(item) =>
            navigate({
              to: '/$org/$host/pages/$pageId',
              params: { org, host, pageId: item.key },
              search: { range },
            })
          }
          renderLabel={(item) => (
            <Link
              to="/$org/$host/pages/$pageId"
              params={{ org, host, pageId: item.key }}
              search={{ range }}
              // The row navigates too, so this one only has to exist for the
              // keyboard, the middle button and the status bar's preview.
              onClick={(event) => event.stopPropagation()}
              className="underline-offset-2 hover:underline"
            >
              {item.label}
            </Link>
          )}
        />
      </SiteSectionBody>
    </>
  )
}
